import React, { useState } from 'react';
import { X, Upload, FileText, Database, Code, Paperclip } from 'lucide-react';
import { publicationAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface MarkAsPublishedModalProps {
  isOpen: boolean;
  onClose: () => void;
  publication: {
    id: number;
    title: string;
    abstract: string;
  };
  onSuccess: () => void;
}

const MarkAsPublishedModal: React.FC<MarkAsPublishedModalProps> = ({
  isOpen,
  onClose,
  publication,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    formalTitle: publication?.title || '',
    finalAbstract: publication?.abstract || '',
    grantId: '',
    doi: '',
    rawDataset: null as File | null,
    preAnalysis: null as File | null,
    code: null as File | null,
    otherAttachment: null as File | null,
    keepCurrentMessage: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({
      ...prev,
      [field]: file
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.formalTitle.trim() || !formData.finalAbstract.trim()) {
      toast.error('Formal title and final abstract are required');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const submitData = new FormData();
      submitData.append('formalTitle', formData.formalTitle);
      submitData.append('finalAbstract', formData.finalAbstract);
      submitData.append('grantId', formData.grantId);
      submitData.append('doi', formData.doi);
      submitData.append('keepCurrentMessage', formData.keepCurrentMessage.toString());
      
      if (formData.rawDataset) {
        submitData.append('rawDataset', formData.rawDataset);
      }
      if (formData.preAnalysis) {
        submitData.append('preAnalysis', formData.preAnalysis);
      }
      if (formData.code) {
        submitData.append('code', formData.code);
      }
      if (formData.otherAttachment) {
        submitData.append('otherAttachment', formData.otherAttachment);
      }

      await publicationAPI.markAsPublished(publication.id, submitData);
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern(`/publications/${publication.id}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success('Publication marked as published successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error marking as published:', error);
      toast.error(error.response?.data?.error || 'Failed to mark as published');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Mark as Published</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Formal Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Formal Title *
            </label>
            <input
              type="text"
              name="formalTitle"
              value={formData.formalTitle}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter the formal publication title"
              required
            />
          </div>

          {/* Final Abstract */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Final Abstract *
            </label>
            <textarea
              name="finalAbstract"
              value={formData.finalAbstract}
              onChange={handleInputChange}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter the final abstract"
              required
            />
          </div>

          {/* Grant ID and DOI */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Grant ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline h-4 w-4 mr-1" />
                Grant ID
              </label>
              <input
                type="text"
                name="grantId"
                value={formData.grantId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="e.g., UM1 AI179699"
              />
            </div>

            {/* DOI */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline h-4 w-4 mr-1" />
                DOI
              </label>
              <input
                type="text"
                name="doi"
                value={formData.doi}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="e.g., 10.1128/aac.01901-24"
              />
            </div>
          </div>

          {/* File Uploads */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Raw Dataset */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Database className="inline h-4 w-4 mr-1" />
                Raw Dataset
              </label>
              <input
                type="file"
                onChange={(e) => handleFileChange(e, 'rawDataset')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                accept=".csv,.xlsx,.xls,.json,.txt,.rds,.sav,.dta"
              />
              {formData.rawDataset && (
                <p className="text-sm text-gray-600 mt-1">
                  Selected: {formData.rawDataset.name}
                </p>
              )}
            </div>

            {/* Pre-analysis */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline h-4 w-4 mr-1" />
                Pre-analysis
              </label>
              <input
                type="file"
                onChange={(e) => handleFileChange(e, 'preAnalysis')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                accept=".pdf,.doc,.docx,.txt,.md"
              />
              {formData.preAnalysis && (
                <p className="text-sm text-gray-600 mt-1">
                  Selected: {formData.preAnalysis.name}
                </p>
              )}
            </div>

            {/* Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Code className="inline h-4 w-4 mr-1" />
                Code
              </label>
              <input
                type="file"
                onChange={(e) => handleFileChange(e, 'code')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                accept=".r,.py,.m,.jl,.sas,.spss,.stata,.zip,.tar.gz"
              />
              {formData.code && (
                <p className="text-sm text-gray-600 mt-1">
                  Selected: {formData.code.name}
                </p>
              )}
            </div>

            {/* Other Attachment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Paperclip className="inline h-4 w-4 mr-1" />
                Other Attachment
              </label>
              <input
                type="file"
                onChange={(e) => handleFileChange(e, 'otherAttachment')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              {formData.otherAttachment && (
                <p className="text-sm text-gray-600 mt-1">
                  Selected: {formData.otherAttachment.name}
                </p>
              )}
            </div>
          </div>

          {/* Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="keepCurrentMessage"
              checked={formData.keepCurrentMessage}
              onChange={handleInputChange}
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              I do not have attachments.
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Publishing...</span>
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  <span>Mark as Published</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MarkAsPublishedModal;
